import type { ErrorResponse } from '@/api/handlers/responses';

/**
 * Mapping every accounts error code to a message with a usable next step.
 *
 * Two rules this file exists to enforce:
 *
 * 1. **Branch on `error.code`, never on the message text.** The message is prose
 *    written for a human and is not a stable contract; the code is.
 * 2. **A field error belongs on its field.** `CODE_DUPLICATE` under the Code
 *    input and `PARENT_HAS_POSTINGS` under the parent picker are actionable;
 *    the same words in a form-level banner make the user hunt for what to fix.
 *
 * The unmapped case is deliberately *not* silent: an unrecognised code falls
 * back to the server's own message, which is more useful than a generic
 * "something went wrong" and makes new contract codes visible rather than
 * swallowed.
 */

/** Where a coded failure should be shown. */
export type AccountErrorTarget = 'code' | 'parentId' | 'form' | 'structural';

export type AccountErrorCode =
    | 'CODE_DUPLICATE'
    | 'PARENT_NOT_FOUND'
    | 'PARENT_CYCLE'
    | 'PARENT_HAS_POSTINGS'
    | 'STRUCTURAL_FIELD_LOCKED_POSTED'
    | 'ARCHIVE_NONZERO_BALANCE'
    | 'ARCHIVE_HAS_ACTIVE_CHILDREN'
    | 'STALE_UPDATE';

type AccountErrorSpec = {
    target: AccountErrorTarget;
    /** i18n key, namespace `pages`. */
    messageKey: string;
};

const SPECS: Record<AccountErrorCode, AccountErrorSpec> = {
    CODE_DUPLICATE: {
        target: 'code',
        messageKey: 'chartOfAccounts.form.errors.codeDuplicate',
    },
    PARENT_NOT_FOUND: {
        target: 'parentId',
        messageKey: 'chartOfAccounts.form.errors.parentNotFound',
    },
    PARENT_CYCLE: {
        target: 'parentId',
        messageKey: 'chartOfAccounts.form.errors.parentCycle',
    },
    PARENT_HAS_POSTINGS: {
        target: 'parentId',
        messageKey: 'chartOfAccounts.form.errors.parentHasPostings',
    },
    STRUCTURAL_FIELD_LOCKED_POSTED: {
        target: 'structural',
        messageKey: 'chartOfAccounts.form.errors.structuralLocked',
    },
    ARCHIVE_NONZERO_BALANCE: {
        target: 'form',
        messageKey: 'chartOfAccounts.form.errors.archiveNonZeroBalance',
    },
    ARCHIVE_HAS_ACTIVE_CHILDREN: {
        target: 'form',
        messageKey: 'chartOfAccounts.form.errors.archiveHasActiveChildren',
    },
    STALE_UPDATE: {
        target: 'form',
        messageKey: 'chartOfAccounts.form.errors.staleUpdate',
    },
};

const isAccountErrorCode = (value: unknown): value is AccountErrorCode =>
    typeof value === 'string' && value in SPECS;

/**
 * A failure, resolved to where it goes and what it says.
 *
 * `messageKey` is `null` for an unmapped code — the caller shows `fallbackText`
 * (the server's own message) instead of inventing one.
 */
export type ResolvedAccountError = {
    code: AccountErrorCode | null;
    target: AccountErrorTarget;
    messageKey: string | null;
    fallbackText: string;
};

export const resolveAccountError = (
    error: ErrorResponse,
): ResolvedAccountError => {
    const code = error.code;
    if (isAccountErrorCode(code)) {
        const spec = SPECS[code];
        return {
            code,
            target: spec.target,
            messageKey: spec.messageKey,
            fallbackText: error.message ?? '',
        };
    }

    // Some deployments answer with the code only in the per-field `errors` map.
    // Accept that shape too rather than degrading to a generic message.
    const fieldCode = error.errors
        ? Object.values(error.errors).find(isAccountErrorCode)
        : undefined;
    if (fieldCode) {
        const spec = SPECS[fieldCode];
        return {
            code: fieldCode,
            target: spec.target,
            messageKey: spec.messageKey,
            fallbackText: error.message ?? '',
        };
    }

    return {
        code: null,
        target: 'form',
        messageKey: null,
        fallbackText: error.message ?? '',
    };
};

/**
 * `409 STALE_UPDATE` — the one failure the form recovers from rather than
 * reports.
 *
 * A bare `409` with no code counts, because the recovery (re-read, compare,
 * replay) is the right response to an unlabelled conflict. A `409` that *does*
 * carry a code does **not**: 409 is also the conventional status for a
 * uniqueness conflict, and this repo's own earlier mock answered a colliding
 * account code with `409 DUPLICATE`. Treating that as staleness would swallow
 * the real error, silently re-read, find no collision, and resubmit the same
 * duplicate code before finally surfacing the second attempt's failure.
 */
export const isStaleUpdate = (error: ErrorResponse): boolean =>
    error.code === 'STALE_UPDATE' ||
    (error.status === 409 && error.code === undefined);
